/**
 * YC demo, delete after the demo, Leo 10-30. Switch `portal_yc_demo` (features.py), never on in prod.
 * Leo's mockup story (10-30, "the mock up exactly"): ticket 037030627L, the feed's $12,500 judgment, was due
 * Fri Oct 16, 14 days overdue; we recommend rescheduling the OATH hearing to avoid the $10,000 fine.
 */
import * as React from "react";

import {
  FLOW_KINDS,
  type ActionItem,
  type ActionPlanFeed,
} from "@/components/portal/useActionPlanFeed";
import { usePortalFeaturesQuery } from "@/components/portal/usePortalFeatures";
import { LL33_KIND, railState } from "../openItems";

export const YC_TICKET = "037030627L";
export const PENALTY = "$12,500";
export const PER_DAY = "$3.08";
export const FINE = "$10,000";
export const OVERDUE_DAYS = 14;
export const WAS_DUE = "Fri Oct 16";
const WAS_DUE_ISO = "2026-10-16";
const WHO_ACTS = "Respondent officer";
/** The rail title's tag, and its tab and Ask-about words (mockup screen 3). */
export const YC_TAG = "DOB : Judgment";
export const YC_CRUMB = "Judgment";
export const YC_SCOPE = "this judgment";
/** The table divider over the ticket's row (mockup screen 2). */
export const YC_GROUP = "Overdue · Needs you";

/** The feed's word for the ticket at HEAD (pay-only row, jo off); YcDeckMark and ycStrip print it. */
export const YC_ACTION = "Pay judgment";

/** Leo 10-31 R2: with the switch on the demo wins over Judgment owed. A judgment row (jo on) reads as the jo-off
 * pay-only row served Oct 30: no judgment keys, HEAD's action, state x undated; subject and money stay. Jo off, or a
 * row of more than one ticket, is returned as is. */
export const ycStrip = (i: ActionItem): ActionItem => {
  if (i.judgmentOwed !== true || i.groupedCount !== 1) return i;
  const { judgmentOwed, judgmentStep, sortBy, ...rest } = i; // eslint-disable-line @typescript-eslint/no-unused-vars
  return { ...rest, action: YC_ACTION, state: "x", nodeAt: "none" };
};

/** action_items.set_next with the ticket as jo off serves it (tier pay_only, after these rows): the building's rows in
 * feed order, boiler and DEP last, as many as the plan lists. */
const nextOff = (
  all: readonly ActionItem[],
  t: ActionItem,
  size: number,
): string[] => {
  const mine = all.filter((i) => i.buildingId === t.buildingId && i !== t);
  return [
    ...mine.filter((i) => !FLOW_KINDS.has(i.kind)),
    ...mine.filter((i) => FLOW_KINDS.has(i.kind)),
  ]
    .slice(0, size)
    .map((i) => i.key);
};

/** The demo shows only with the switch on and only on the item carrying the ticket (grouping changes item.key). */
export const isYcDemo = (on: boolean, item: ActionItem | null): boolean =>
  on && !!item?.recordIds.includes(YC_TICKET);

/** The rewritten rows: the table draws them first and the deck gives them their own past-date node. */
const demoRows = new WeakSet<ActionItem>();
export const isYcRow = (item: ActionItem): boolean => demoRows.has(item);

/** Switch on: the ticket's item is overdue since Fri Oct 16 on every surface (mockup screen 2); money untouched.
 * The legend moves its one count to Past date to match. */
export const ycFeed = (feed: ActionPlanFeed, on: boolean): ActionPlanFeed => {
  const hit = (i: ActionItem) => i.recordIds.includes(YC_TICKET);
  if (!on || ![...feed.items, ...feed.later].some(hit)) return feed;
  const swap = (i: ActionItem): ActionItem => {
    if (!hit(i)) return i;
    const row: ActionItem = {
      ...ycStrip(i),
      actBy: WAS_DUE_ISO,
      adviceBy: null,
      nodeAt: "past",
      state: "past",
      whoActs: WHO_ACTS,
    };
    demoRows.add(row);
    return row;
  };
  const yc = [...feed.items, ...feed.later].find(hit);
  const was = yc && railState(feed, yc);
  const step = (s: string) => (s === was ? -1 : 0) + (s === "past" ? 1 : 0);
  return {
    ...feed,
    items: feed.items.map(swap),
    later: feed.later.map(swap),
    rail: feed.rail.map((r) => ({ ...r, count: r.count + step(r.state) })),
  };
};

/** Mockup screen 1: the ticket shows as the feed has it ("Pay judgment", $12,500 balance), third, after the first
 * two Next items that are not LL33; no "+N more"; Owed reads one red Overdue row. */
const ycPlans = new WeakSet<object>();
export const isYcPlan = (plan: object | undefined): boolean =>
  !!plan && ycPlans.has(plan);

export const ycPanel = (
  feed: ActionPlanFeed | undefined,
  buildingId: string,
  on: boolean,
): ActionPlanFeed | undefined => {
  if (!feed || !on) return feed;
  const plan = feed.buildings[buildingId];
  const all = [...feed.items, ...feed.later];
  const t = all.find((i) => i.recordIds.includes(YC_TICKET));
  if (!plan || !t || t.buildingId !== buildingId) return feed;
  const kind = new Map(all.map((i) => [i.key, i.kind]));
  // R2: jo on lists the ticket first and pushes a row out; read the list jo off serves.
  const served =
    ycStrip(t) === t ? plan.next : nextOff(all, t, plan.next.length);
  const lead = served
    .filter((k) => k !== t.key && kind.get(k) !== LL33_KIND)
    .slice(0, 2);
  const yc = { ...plan, next: [...lead, t.key], moreCount: 0 };
  ycPlans.add(yc);
  // R2: the Next line reads the stripped row, so no judgment date or step word shows beside YcOverdue.
  const strip = (i: ActionItem) => (i === t ? ycStrip(i) : i);
  return {
    ...feed,
    items: feed.items.map(strip),
    later: feed.later.map(strip),
    buildings: { ...feed.buildings, [buildingId]: yc },
  };
};

/** BuildingRowPanel's one hook; reads the cached switch (no fetch), as Page.tsx does. */
export const useYcPanel = (
  feed: ActionPlanFeed | undefined,
  buildingId: string,
): ActionPlanFeed | undefined => {
  const on = usePortalFeaturesQuery(false).data?.ycDemo === true;
  return React.useMemo(
    () => ycPanel(feed, buildingId, on),
    [feed, buildingId, on],
  );
};
